import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { 
  FiPlus, 
  FiSearch, 
  FiBook, 
  FiCalendar, 
  FiCheckCircle, 
  FiClock,
  FiXCircle,
  FiAlertCircle,
  FiX,
  FiSave,
  FiTrash2,
  FiEye,
  FiPrinter,
  FiInfo
} from 'react-icons/fi';
import './KRSMahasiswa.css';

const KRSMahasiswa = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('daftar'); // 'daftar' or 'isi'
  const [krsList, setKrsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState('');
  const [availableSemesters, setAvailableSemesters] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [totalSKS, setTotalSKS] = useState(0);
  const [maxSKS, setMaxSKS] = useState(24); // Default max SKS
  const [searchCourse, setSearchCourse] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedKRSDetail, setSelectedKRSDetail] = useState(null);

  const fetchKRSList = useCallback(async () => {
    if (!selectedSemester) return;
    
    setLoading(true);
    try {
      // Use same endpoint as admin portal
      const res = await axios.get('/api/krs', { 
        params: { 
          nim: user.nim,
          semester: selectedSemester,
          limit: 1000 
        } 
      });
      setKrsList(res.data.data || []);
    } catch (error) {
      console.error('Error fetching KRS:', error);
      if (error.response?.status === 404) {
        setKrsList([]);
      } else {
        toast.error('Gagal memuat data KRS');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedSemester, user.nim]);

  const fetchAvailableSemesters = useCallback(async () => {
    try {
      // Get unique semesters from KRS data (same as admin portal)
      const res = await axios.get('/api/krs', { 
        params: { 
          nim: user.nim,
          limit: 1000 
        } 
      });
      
      const krsData = res.data.data || [];
      
      // Extract unique semesters from KRS
      const semesterMap = new Map();
      
      krsData.forEach(krs => {
        if (krs.id_semester && !semesterMap.has(krs.id_semester)) {
          semesterMap.set(krs.id_semester, {
            id_semester: krs.id_semester,
            nama_semester: krs.nama_semester || krs.id_semester
          });
        }
      });
      
      // Convert map to array and sort by id_semester descending
      const semesters = Array.from(semesterMap.values())
        .sort((a, b) => b.id_semester.localeCompare(a.id_semester));
      
      setAvailableSemesters(semesters);
      if (semesters.length > 0) {
        setSelectedSemester(semesters[0].id_semester);
      }
    } catch (error) {
      console.error('Error fetching semesters:', error);
      toast.error('Gagal memuat daftar semester');
    }
  }, [user.nim]);

  useEffect(() => {
    fetchAvailableSemesters();
  }, [fetchAvailableSemesters]);

  useEffect(() => {
    if (selectedSemester) {
      fetchKRSList();
    }
  }, [selectedSemester, fetchKRSList]);

  useEffect(() => {
    const total = selectedCourses.reduce((sum, course) => sum + (course.sks_mata_kuliah || 0), 0);
    setTotalSKS(total);
  }, [selectedCourses]);

  const fetchAvailableCourses = async () => {
    try {
      const res = await axios.get(`/api/krs/available-courses/${selectedSemester}`);
      setAvailableCourses(res.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Gagal memuat mata kuliah');
    }
  };

  const handleOpenAddModal = () => {
    setShowAddModal(true);
    fetchAvailableCourses();
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setSelectedCourses([]);
    setSearchCourse('');
  };

  const handleSelectCourse = (course) => {
    // Check if already selected
    if (selectedCourses.find(c => c.kode_mata_kuliah === course.kode_mata_kuliah)) {
      toast.warning('Mata kuliah sudah dipilih');
      return;
    }

    // Check if already in KRS
    if (krsList.find(k => k.kode_mata_kuliah === course.kode_mata_kuliah)) {
      toast.warning('Mata kuliah sudah ada di KRS');
      return;
    }

    // Check max SKS
    const newTotal = totalSKS + (course.sks_mata_kuliah || 0);
    if (newTotal > maxSKS) {
      toast.error(`Total SKS tidak boleh melebihi ${maxSKS}`);
      return;
    }

    setSelectedCourses([...selectedCourses, course]);
  };

  const handleRemoveCourse = (kode) => {
    setSelectedCourses(selectedCourses.filter(c => c.kode_mata_kuliah !== kode));
  };

  const handleSubmitKRS = async () => {
    if (selectedCourses.length === 0) {
      toast.warning('Pilih minimal 1 mata kuliah');
      return;
    }

    try {
      // Get mahasiswa data first
      const studentRes = await axios.get(`/api/students/nim/${user.nim}`);
      const studentData = studentRes.data.data;

      // Prepare KRS data same as admin portal
      const krsData = selectedCourses.map(course => ({
        nim: user.nim,
        nama_mahasiswa: studentData.nama || user.name,
        kode_mata_kuliah: course.kode_mata_kuliah,
        nama_mata_kuliah: course.nama_mata_kuliah,
        sks_mata_kuliah: course.sks_mata_kuliah,
        id_semester: selectedSemester,
        nama_semester: availableSemesters.find(s => s.id_semester === selectedSemester)?.nama_semester,
        nama_kelas_kuliah: 'A',
        id_prodi: studentData.id_prodi,
        nama_program_studi: studentData.nama_program_studi,
        approval_status: 'pending',
        approval_notes: '',
        status_sync: 'waiting'
      }));

      // Use same endpoint structure as admin
      const res = await axios.post('/api/krs/mahasiswa/submit', { krsData });
      
      if (res.data.success) {
        toast.success('KRS berhasil diajukan! Menunggu persetujuan dosen PA');
        handleCloseAddModal();
        fetchKRSList();
        fetchAvailableSemesters(); // Refresh semesters in case new semester added
      }
    } catch (error) {
      console.error('Error submitting KRS:', error);
      toast.error(error.response?.data?.message || 'Gagal mengajukan KRS');
    }
  };

  const handleDeleteKRS = async (id) => {
    if (!window.confirm('Yakin ingin menghapus mata kuliah ini dari KRS?')) {
      return;
    }

    try {
      const res = await axios.delete(`/api/krs/${id}`);
      if (res.data.success) {
        toast.success('Mata kuliah berhasil dihapus');
        fetchKRSList();
      }
    } catch (error) {
      console.error('Error deleting KRS:', error);
      toast.error('Gagal menghapus mata kuliah');
    }
  };

  const handleViewDetail = (krs) => {
    setSelectedKRSDetail(krs);
    setShowDetailModal(true);
  };

  const handlePrintKRS = () => {
    if (!selectedSemester) {
      toast.error('Pilih semester terlebih dahulu');
      return;
    }

    if (krsList.length === 0) {
      toast.error('Tidak ada KRS untuk dicetak');
      return;
    }

    // Open print page in new window
    const printUrl = `http://localhost:5000/api/krs/print/${user.nim}/${selectedSemester}`;
    window.open(printUrl, '_blank');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="status-badge approved"><FiCheckCircle /> Disetujui</span>;
      case 'pending':
        return <span className="status-badge pending"><FiClock /> Menunggu</span>;
      case 'rejected':
        return <span className="status-badge rejected"><FiXCircle /> Ditolak</span>;
      default:
        return <span className="status-badge pending"><FiAlertCircle /> -</span>;
    }
  };

  const filteredCourses = availableCourses.filter(course => {
    const search = searchCourse.toLowerCase();
    return (
      course.kode_mata_kuliah?.toLowerCase().includes(search) ||
      course.nama_mata_kuliah?.toLowerCase().includes(search)
    );
  });

  const calculateTotalSKS = () => {
    return krsList.reduce((sum, krs) => sum + (krs.sks_mata_kuliah || 0), 0);
  };

  return (
    <div className="krs-mahasiswa-container">
      {/* Header */}
      <div className="krs-header">
        <div className="header-content">
          <h1><FiBook /> Kartu Rencana Studi (KRS)</h1>
          <p>Kelola rencana studi Anda untuk semester ini</p>
        </div>
        <div className="header-info">
          <div className="info-card">
            <span className="info-label">NIM</span>
            <span className="info-value">{user?.nim}</span>
          </div>
          <div className="info-card">
            <span className="info-label">Nama</span>
            <span className="info-value">{user?.name}</span>
          </div>
        </div>
      </div>

      {/* Semester Selector */}
      <div className="semester-selector">
        <label><FiCalendar /> Pilih Semester:</label>
        <select 
          value={selectedSemester} 
          onChange={(e) => setSelectedSemester(e.target.value)}
          className="semester-select"
        >
          {availableSemesters.map(sem => (
            <option key={sem.id_semester} value={sem.id_semester}>
              {sem.nama_semester}
            </option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div className="krs-tabs">
        <button 
          className={`tab-btn ${activeTab === 'daftar' ? 'active' : ''}`}
          onClick={() => setActiveTab('daftar')}
        >
          <FiBook /> Daftar KRS
        </button>
        <button 
          className={`tab-btn ${activeTab === 'isi' ? 'active' : ''}`}
          onClick={() => setActiveTab('isi')}
        >
          <FiPlus /> Isi KRS
        </button>
      </div>

      {/* Content */}
      {activeTab === 'daftar' ? (
        <div className="krs-content">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Memuat data KRS...</p>
            </div>
          ) : krsList.length === 0 ? (
            <div className="empty-state">
              <FiAlertCircle />
              <h3>Belum Ada KRS</h3>
              <p>Anda belum mengisi KRS untuk semester ini</p>
              <button className="btn-primary" onClick={() => setActiveTab('isi')}>
                <FiPlus /> Isi KRS Sekarang
              </button>
            </div>
          ) : (
            <>
              <div className="krs-summary">
                <div className="summary-card">
                  <h4>Total Mata Kuliah</h4>
                  <p className="summary-value">{krsList.length}</p>
                </div>
                <div className="summary-card">
                  <h4>Total SKS</h4>
                  <p className="summary-value">{calculateTotalSKS()}</p>
                </div>
                <div className="summary-card">
                  <h4>Disetujui</h4>
                  <p className="summary-value success">
                    {krsList.filter(k => k.approval_status === 'approved').length}
                  </p>
                </div>
                <div className="summary-card">
                  <h4>Menunggu</h4>
                  <p className="summary-value warning">
                    {krsList.filter(k => k.approval_status === 'pending').length}
                  </p>
                </div>
              </div>

              <div className="krs-actions-bar">
                <div className="print-info">
                  <FiInfo />
                  <span>Cetak KRS dalam format resmi dengan QR Code verifikasi</span>
                </div>
                <button className="btn-print" onClick={handlePrintKRS}>
                  <FiPrinter /> Cetak KRS (PDF)
                </button>
              </div>

              <div className="krs-table-wrapper">
                <table className="krs-table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Kode MK</th>
                      <th>Nama Mata Kuliah</th>
                      <th>SKS</th>
                      <th>Kelas</th>
                      <th>Status Approval</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {krsList.map((krs, index) => (
                      <tr key={krs._id}>
                        <td>{index + 1}</td>
                        <td>{krs.kode_mata_kuliah}</td>
                        <td className="nama-mk">{krs.nama_mata_kuliah}</td>
                        <td>{krs.sks_mata_kuliah}</td>
                        <td>{krs.nama_kelas_kuliah}</td>
                        <td>{getStatusBadge(krs.approval_status)}</td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-action view"
                              onClick={() => handleViewDetail(krs)}
                              title="Lihat Detail"
                            >
                              <FiEye />
                            </button>
                            {krs.approval_status === 'pending' && (
                              <button 
                                className="btn-action delete"
                                onClick={() => handleDeleteKRS(krs._id)}
                                title="Hapus"
                              >
                                <FiTrash2 />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="krs-content">
          <div className="isi-krs-section">
            <div className="isi-krs-header">
              <h3>Tambah Mata Kuliah ke KRS</h3>
              <button className="btn-primary" onClick={handleOpenAddModal}>
                <FiPlus /> Pilih Mata Kuliah
              </button>
            </div>

            {selectedCourses.length > 0 ? (
              <>
                <div className="selected-courses">
                  <h4>Mata Kuliah yang Dipilih</h4>
                  <div className="selected-courses-list">
                    {selectedCourses.map((course, index) => (
                      <div key={course.kode_mata_kuliah} className="selected-course-item">
                        <div className="course-number">{index + 1}</div>
                        <div className="course-info">
                          <div className="course-code">{course.kode_mata_kuliah}</div>
                          <div className="course-name">{course.nama_mata_kuliah}</div>
                        </div>
                        <div className="course-sks">{course.sks_mata_kuliah} SKS</div>
                        <button 
                          className="btn-remove"
                          onClick={() => handleRemoveCourse(course.kode_mata_kuliah)}
                        >
                          <FiX />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="krs-footer">
                  <div className="total-sks">
                    <span>Total SKS:</span>
                    <span className={`sks-value ${totalSKS > maxSKS ? 'exceed' : ''}`}>
                      {totalSKS} / {maxSKS}
                    </span>
                  </div>
                  <div className="footer-actions">
                    <button className="btn-secondary" onClick={handleCloseAddModal}>
                      Batal
                    </button>
                    <button 
                      className="btn-primary" 
                      onClick={handleSubmitKRS}
                      disabled={totalSKS > maxSKS}
                    >
                      <FiSave /> Ajukan KRS
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-selection">
                <FiAlertCircle />
                <p>Belum ada mata kuliah yang dipilih</p>
                <p className="hint">Klik tombol "Pilih Mata Kuliah" untuk menambahkan</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Pilih Mata Kuliah */}
      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseAddModal}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiBook /> Pilih Mata Kuliah</h3>
              <button className="btn-close" onClick={handleCloseAddModal}>
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <div className="search-box">
                <FiSearch />
                <input
                  type="text"
                  placeholder="Cari mata kuliah..."
                  value={searchCourse}
                  onChange={(e) => setSearchCourse(e.target.value)}
                />
              </div>

              <div className="courses-list">
                {filteredCourses.map(course => (
                  <div 
                    key={course.kode_mata_kuliah} 
                    className={`course-item ${selectedCourses.find(c => c.kode_mata_kuliah === course.kode_mata_kuliah) ? 'selected' : ''}`}
                    onClick={() => handleSelectCourse(course)}
                  >
                    <div className="course-details">
                      <div className="course-code">{course.kode_mata_kuliah}</div>
                      <div className="course-name">{course.nama_mata_kuliah}</div>
                    </div>
                    <div className="course-sks">{course.sks_mata_kuliah} SKS</div>
                  </div>
                ))}
              </div>

              {filteredCourses.length === 0 && (
                <div className="empty-state">
                  <FiAlertCircle />
                  <p>Tidak ada mata kuliah tersedia</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <div className="selected-info">
                Dipilih: {selectedCourses.length} mata kuliah ({totalSKS} SKS)
              </div>
              <button className="btn-primary" onClick={handleCloseAddModal}>
                Selesai
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detail KRS */}
      {showDetailModal && selectedKRSDetail && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiBook /> Detail KRS</h3>
              <button className="btn-close" onClick={() => setShowDetailModal(false)}>
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Kode Mata Kuliah:</span>
                <span className="detail-value">{selectedKRSDetail.kode_mata_kuliah}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Nama Mata Kuliah:</span>
                <span className="detail-value">{selectedKRSDetail.nama_mata_kuliah}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">SKS:</span>
                <span className="detail-value">{selectedKRSDetail.sks_mata_kuliah}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Kelas:</span>
                <span className="detail-value">{selectedKRSDetail.nama_kelas_kuliah}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Semester:</span>
                <span className="detail-value">{selectedKRSDetail.nama_semester}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status Approval:</span>
                {getStatusBadge(selectedKRSDetail.approval_status)}
              </div>
              {selectedKRSDetail.approval_by && (
                <div className="detail-row">
                  <span className="detail-label">Dosen PA:</span>
                  <span className="detail-value">{selectedKRSDetail.approval_by}</span>
                </div>
              )}
              {selectedKRSDetail.approval_notes && (
                <div className="detail-row">
                  <span className="detail-label">Catatan:</span>
                  <span className="detail-value">{selectedKRSDetail.approval_notes}</span>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetailModal(false)}>
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KRSMahasiswa;
